// Hooks de datos de Modelia. Aquí no se arma ninguna URL: eso vive en `api/services/loan-scoring/`
// y en `api/services/loan-config/`.
import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SciEvaluationResponse } from '@fiado/type-kit/bin/loanScoring/dtos/responses/SciEvaluationResponse';
import {
    listParameters,
    PARAMETERS_QUERY_KEY,
} from '@/api/services/loan-config/loan-config.client';
import { listSciEvaluations, rerunSciEvaluation } from '@/api/services/loan-scoring';
import { userMessageOf } from '@/api/errors';
import { groupScoringParameters } from '../logic';
import type { ModeliaEvaluationRow, ModeliaListCoverage, ScoringParameterGroup } from '../types';

const EVALUATIONS_KEY = ['modelia-evaluations'] as const;

/** Filas por página del servidor. El botón «Cargar más» pide la siguiente y la suma a la pila. */
export const EVALUATIONS_PAGE_SIZE = 50;

export interface ModeliaEvaluationsResult {
    /** Todas las páginas ya bajadas, aplanadas y con referencia estable. */
    rows: ModeliaEvaluationRow[];
    coverage: ModeliaListCoverage;
    isLoading: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    /**
     * El fallo que deja la pantalla SIN lista; un 403 nombra `loan.scoring.read`. Con páginas ya
     * cargadas es `null`: el tropiezo de la siguiente viaja en `nextPageError` y las filas se quedan.
     */
    error: unknown;
    /** Por qué falló la ÚLTIMA página pedida. `null` = no falló; las filas de antes siguen. */
    nextPageError: string | null;
    refetch: () => void;
}

/** El listado del tenant, página por página. Una sola llamada por página: el motor entrega la fila
 *  ya resuelta y la pantalla no cruza nada. */
export function useModeliaEvaluations(): ModeliaEvaluationsResult {
    const query = useInfiniteQuery({
        queryKey: EVALUATIONS_KEY,
        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            listSciEvaluations({ limit: EVALUATIONS_PAGE_SIZE, nextToken: pageParam }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken,
        staleTime: 60_000,
    });

    // useMemo OBLIGATORIO: useReactTable compara `data` por referencia. Sin memo el flatMap devuelve
    // un array nuevo en cada render y la tabla entra en re-render infinito.
    const rows = useMemo<ModeliaEvaluationRow[]>(
        () => query.data?.pages.flatMap((page) => page.items) ?? [],
        [query.data],
    );

    const coverage = useMemo<ModeliaListCoverage>(
        () => ({
            loadedCount: rows.length,
            hasMore: query.hasNextPage,
            unresolvedCustomerCount:
                query.data?.pages.reduce(
                    (total, page) => total + page.unresolvedCustomerCount,
                    0,
                ) ?? 0,
            withoutCustomerCount: rows.filter((row) => row.retailCustomerId === null).length,
        }),
        [rows, query.data, query.hasNextPage],
    );

    return {
        rows,
        coverage,
        isLoading: query.isLoading,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage,
        fetchNextPage: () => {
            void query.fetchNextPage();
        },
        error: query.data ? null : query.error,
        nextPageError: query.isFetchNextPageError ? userMessageOf(query.error) : null,
        refetch: () => {
            void query.refetch();
        },
    };
}

export interface RerunSciEvaluationResult {
    /** Corre el análisis y resuelve con el snapshot NUEVO, ya con el listado releído. */
    rerun: (evaluationId: string) => Promise<SciEvaluationResponse>;
    isRunning: boolean;
}

/**
 * Vuelve a correr una evaluación. Re-correr AGREGA una fila: la vieja sigue en el listado como
 * historial. El `await` del invalidate deja el listado ya releído cuando la promesa resuelve, para
 * que la pantalla pueda seleccionar la nueva sin que la ficha parpadee en vacío.
 */
export function useRerunSciEvaluation(): RerunSciEvaluationResult {
    const queryClient = useQueryClient();
    const mutation = useMutation<SciEvaluationResponse, Error, string>({
        mutationFn: (evaluationId) => rerunSciEvaluation(evaluationId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: EVALUATIONS_KEY }),
    });

    return {
        rerun: (evaluationId) => mutation.mutateAsync(evaluationId),
        isRunning: mutation.isPending,
    };
}

export interface ScoringParametersResult {
    groups: ScoringParameterGroup[];
    isLoading: boolean;
    error: unknown;
    refetch: () => void;
}

/** Los parámetros del modelo. Solo lectura: la edición vive en Configuración global. */
export function useScoringParameters(enabled: boolean): ScoringParametersResult {
    const query = useQuery({
        queryKey: PARAMETERS_QUERY_KEY,
        queryFn: listParameters,
        enabled,
        staleTime: 60_000,
    });

    const groups = useMemo(
        () => groupScoringParameters(Object.values(query.data ?? {}).flat()),
        [query.data],
    );

    return {
        groups,
        // Con el tab deshabilitado react-query deja `isLoading` en `true`: eso pintaría un
        // cargando eterno donde en realidad no se pidió nada.
        isLoading: enabled && query.isLoading,
        error: enabled ? query.error : null,
        refetch: () => {
            void query.refetch();
        },
    };
}
